import { passwordSchema } from '@/api/auth/auth.schema';
import { TextField } from '@/components/enc/fields';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useForgotPassword, useResetPassword, useSignIn } from '@/services/auth';
import {
  Calendar,
  Check,
  ChevronLeft,
  ClipboardList,
  Info,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const EMAIL_RE = /^\S+@\S+\.\S+$/;
const EASE = 'ease-[cubic-bezier(.2,.8,.2,1)]';

const FEATURES: [LucideIcon, string][] = [
  [Users, 'Casais inscritos e fichas completas'],
  [Calendar, 'Encontros, vagas e aniversários'],
  [ClipboardList, 'Listas e fichas prontas para imprimir'],
];

function VisualPanel() {
  return (
    <aside className="relative isolate flex min-h-[220px] flex-col justify-between gap-7 overflow-hidden px-5 pt-6 pb-12 text-white min-[600px]:min-h-[260px] min-[960px]:min-h-screen min-[960px]:flex-[1_1_52%] min-[960px]:px-12 min-[960px]:py-10">
      <img
        src="/assets/about-2.jpg"
        alt=""
        className="absolute inset-0 -z-10 size-full object-cover [filter:grayscale(.35)_contrast(1.05)]"
      />
      <div className="absolute inset-0 -z-10 bg-[linear-gradient(180deg,rgba(26,8,10,.55)_0%,rgba(60,14,18,.55)_45%,rgba(26,8,10,.92)_100%)]" />
      <div className="absolute inset-0 -z-10 bg-[rgba(163,38,43,.22)] mix-blend-multiply" />

      <Link to="/" className="flex w-fit items-center gap-3">
        <span className="flex size-10 items-center justify-center rounded-[11px] border border-white/28 bg-white/6">
          <img src="/assets/logo.png" alt="" className="h-[22px] w-auto" />
        </span>
        <span className="flex flex-col">
          <span className="font-display text-lg leading-tight font-extrabold tracking-[-0.02em]">
            Enchei-vos
          </span>
          <span className="text-[10px] font-bold tracking-[.14em] text-white/72">
            RETIRO DE CASAIS
          </span>
        </span>
      </Link>

      <div className="hidden max-w-[520px] flex-col gap-[22px] min-[960px]:flex">
        <div className="flex items-center gap-2.5">
          <span className="h-px w-7 bg-white/60" />
          <span className="text-[11px] font-bold tracking-[.14em] text-white/80">
            ÁREA DA EQUIPE
          </span>
        </div>
        <h2 className="m-0 font-display text-[42px] leading-[1.06] font-extrabold tracking-[-0.03em] text-balance min-[1280px]:text-[52px]">
          Cuidar de cada casal começa pela organização.
        </h2>
        <p className="m-0 max-w-[440px] text-base leading-[1.55] text-white/82">
          Inscrições, encontros e fichas impressas do retiro em um só lugar, para a equipe
          servir com tranquilidade.
        </p>
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {FEATURES.map(([Icon, text]) => (
            <li key={text} className="flex items-center gap-3 text-sm text-white/90">
              <span className="flex size-8 items-center justify-center rounded-[9px] border border-white/22 bg-white/6">
                <Icon className="size-4" strokeWidth={1.6} />
              </span>
              {text}
            </li>
          ))}
        </ul>
      </div>

      <div className="hidden items-center justify-between border-t border-white/16 pt-5 text-xs text-white/66 min-[960px]:flex">
        <span>Comunidade Católica Apóstolos</span>
        <div className="flex">
          {['about-1', 'about-3', 'about-2'].map((img, i) => (
            <img
              key={img}
              src={`/assets/${img}.jpg`}
              alt=""
              className={cn(
                'size-[34px] rounded-full border-2 border-[#2A1012] object-cover',
                i > 0 && '-ml-2.5',
              )}
            />
          ))}
        </div>
      </div>
    </aside>
  );
}

function Heading({ eyebrow, title, text }: { eyebrow: string; title: string; text: string }) {
  return (
    <div className="flex flex-col gap-2.5">
      <span className="text-[11px] font-bold tracking-[.12em] text-[#A3262B]">{eyebrow}</span>
      <h1 className="m-0 font-display text-[32px] leading-[1.1] font-extrabold tracking-[-0.03em] text-[#0B0B0C]">
        {title}
      </h1>
      <p className="m-0 text-[15px] leading-normal text-[#6B6B70]">{text}</p>
    </div>
  );
}

function Notice({ tone, children }: { tone: 'error' | 'info'; children: ReactNode }) {
  const Icon = tone === 'error' ? Info : Check;
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-2.5 rounded-[12px] border px-3.5 py-3 text-[13px] leading-[1.45]',
        tone === 'error'
          ? 'border-[rgba(163,38,43,.25)] bg-[#FBEDEC] text-[#7F1D22]'
          : 'border-[rgba(31,157,99,.3)] bg-[#E7F6EE] text-[#14653F]',
      )}
    >
      <Icon className="mt-px size-4 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

const primaryBtn = cn(
  'flex h-[52px] w-full items-center justify-center gap-2.5 rounded-[12px] bg-[#A3262B] text-[15px] font-semibold text-white transition-[background-color,transform] duration-[120ms] hover:bg-[#8E1F24] active:scale-[.98] disabled:opacity-80',
  EASE,
);

/**
 * Recuperação de senha em duas etapas: (1) e-mail → o backend manda um código de 6 dígitos;
 * (2) código + nova senha. A resposta da etapa 1 é sempre de sucesso, exista a conta ou não.
 */
function ForgotPassword({
  email,
  setEmail,
  onBack,
}: {
  email: string;
  setEmail: (v: string) => void;
  onBack: () => void;
}) {
  const toast = useToast();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState('');

  const forgot = useForgotPassword();
  const reset = useResetPassword();
  const error = localError || forgot.error?.message || reset.error?.message;
  const busy = forgot.isPending || reset.isPending || reset.isSuccess;

  const clearErrors = () => {
    setLocalError('');
    if (forgot.error) forgot.reset();
    if (reset.error) reset.reset();
  };

  const sendCode = (e: FormEvent) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) return setLocalError('Informe um e-mail válido.');
    setLocalError('');
    forgot.mutate({ email: email.trim() }, { onSuccess: () => setStep('code') });
  };

  const resend = () =>
    forgot.mutate(
      { email: email.trim() },
      { onSuccess: () => toast.success('Se o e-mail estiver cadastrado, enviamos um novo código.') },
    );

  const submitReset = (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) return setLocalError('O código tem 6 dígitos.');
    const parsed = passwordSchema.safeParse(password);
    if (!parsed.success) return setLocalError(parsed.error.issues[0]?.message ?? 'Senha inválida.');
    if (password !== confirm) return setLocalError('As senhas não conferem.');

    setLocalError('');
    reset.mutate(
      { email: email.trim(), otp, newPassword: password },
      {
        onSuccess: () => {
          toast.success('Senha redefinida! Entre com a nova senha.');
          onBack();
        },
      },
    );
  };

  return (
    <>
      <button
        type="button"
        onClick={onBack}
        className="-mb-3 flex min-h-11 w-fit items-center gap-1 text-sm font-semibold text-[#4A4A4F] transition-colors hover:text-[#A3262B]"
      >
        <ChevronLeft className="size-4" /> Voltar ao login
      </button>

      <Heading
        eyebrow="RECUPERAR ACESSO"
        title={step === 'email' ? 'Redefinir senha' : 'Nova senha'}
        text={
          step === 'email'
            ? 'Informe seu e-mail e enviaremos um código de 6 dígitos para criar uma nova senha.'
            : 'Digite o código que enviamos e escolha a nova senha. O código vale por 10 minutos.'
        }
      />

      {step === 'code' && !error && (
        <Notice tone="info">
          Se o e-mail <strong>{email.trim()}</strong> estiver cadastrado, você receberá o código em
          instantes.
        </Notice>
      )}
      {error && <Notice tone="error">{error}</Notice>}

      <form
        noValidate
        onSubmit={step === 'email' ? sendCode : submitReset}
        className="flex flex-col gap-[18px]"
      >
        {step === 'email' ? (
          <TextField
            label="E-mail"
            type="email"
            autoComplete="email"
            placeholder="voce@email.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              clearErrors();
            }}
          />
        ) : (
          <>
            <TextField
              label="Código"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              className="font-display text-lg font-bold tracking-[.3em]"
              value={otp}
              onChange={(e) => {
                setOtp(e.target.value.replace(/\D/g, ''));
                clearErrors();
              }}
            />
            <TextField
              label="Nova senha"
              type="password"
              autoComplete="new-password"
              placeholder="Nova senha"
              hint="Mínimo de 8 caracteres, com maiúscula, minúscula, número e símbolo."
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                clearErrors();
              }}
            />
            <TextField
              label="Confirmar nova senha"
              type="password"
              autoComplete="new-password"
              placeholder="Repita a nova senha"
              value={confirm}
              onChange={(e) => {
                setConfirm(e.target.value);
                clearErrors();
              }}
            />
          </>
        )}

        <button type="submit" disabled={busy} className={primaryBtn}>
          {busy && (
            <span className="size-4 animate-spin rounded-full border-2 border-white/35 border-t-white [animation-duration:.7s]" />
          )}
          {step === 'email'
            ? busy
              ? 'Enviando...'
              : 'Enviar código'
            : busy
              ? 'Salvando...'
              : 'Redefinir senha'}
        </button>

        {step === 'code' && (
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <button
              type="button"
              onClick={() => {
                setStep('email');
                clearErrors();
              }}
              className="min-h-11 font-semibold text-[#4A4A4F] transition-colors hover:text-[#A3262B]"
            >
              Trocar e-mail
            </button>
            <button
              type="button"
              onClick={resend}
              disabled={busy}
              className="min-h-11 font-semibold text-[#A3262B] transition-colors hover:text-[#7F1D22] disabled:opacity-60"
            >
              Reenviar código
            </button>
          </div>
        )}
      </form>
    </>
  );
}

const SignInPage = () => {
  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState('');

  const { mutate: signIn, isPending } = useSignIn();

  const emailError = touched && !EMAIL_RE.test(email) ? 'Informe um e-mail válido.' : '';
  const passwordError =
    touched && password.length < 6 ? 'A senha tem pelo menos 6 caracteres.' : '';

  const switchMode = (next: 'login' | 'forgot') => {
    setMode(next);
    setTouched(false);
    setError('');
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!EMAIL_RE.test(email) || password.length < 6) return;
    signIn(
      { email, password, remember },
      {
        onError: () =>
          setError('E-mail ou senha incorretos. Confira os dados e tente novamente.'),
      },
    );
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#F6F6F4] font-[family-name:var(--font-ui)] text-[#2E2E33] min-[960px]:flex-row">
      <Helmet>
        <title>Entrar | Enchei-vos Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <VisualPanel />

      <main className="relative z-10 -mt-6 flex flex-1 flex-col rounded-t-[24px] bg-[#F6F6F4] px-5 pt-8 pb-6 min-[600px]:px-12 min-[600px]:pt-10 min-[960px]:mt-0 min-[960px]:flex-[1_1_48%] min-[960px]:rounded-none min-[960px]:pb-7">
        <div className="flex flex-1 items-center justify-center">
          <div
            key={mode}
            className={cn(
              'flex w-full max-w-[400px] flex-col gap-7 animate-in fade-in-0 slide-in-from-bottom-2 duration-[280ms]',
              EASE,
            )}
          >
            {mode === 'login' ? (
              <>
                <Heading
                  eyebrow="ENCHEI-VOS ADMIN"
                  title="Entrar no painel"
                  text="Use o e-mail e a senha cadastrados pelo administrador da equipe."
                />

                {error && <Notice tone="error">{error}</Notice>}

                <form noValidate onSubmit={handleSubmit} className="flex flex-col gap-[18px]">
                  <TextField
                    label="E-mail"
                    type="email"
                    autoComplete="email"
                    placeholder="voce@email.com"
                    value={email}
                    error={emailError}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError('');
                    }}
                  />

                  <div className="relative">
                    <TextField
                      label="Senha"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="Sua senha"
                      className="pr-24"
                      value={password}
                      error={passwordError}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setError('');
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      className="absolute top-[37px] right-3 h-8 rounded-[8px] px-2.5 text-xs font-bold text-[#A3262B] transition-colors duration-[120ms] hover:bg-[#FBEDEC]"
                    >
                      {showPassword ? 'Ocultar' : 'Mostrar'}
                    </button>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <label className="flex min-h-11 cursor-pointer items-center gap-2.5 text-sm text-[#4A4A4F]">
                      <input
                        type="checkbox"
                        checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                        className="peer sr-only"
                      />
                      <span
                        className={cn(
                          'flex size-5 items-center justify-center rounded-[6px] border transition-colors duration-[120ms] peer-focus-visible:ring-[3px] peer-focus-visible:ring-[#A3262B]/25',
                          remember
                            ? 'border-[#A3262B] bg-[#A3262B]'
                            : 'border-[rgba(11,11,12,.25)] bg-white',
                        )}
                      >
                        {remember && <Check className="size-3.5 text-white" strokeWidth={3} />}
                      </span>
                      Manter conectado
                    </label>
                    <button
                      type="button"
                      onClick={() => switchMode('forgot')}
                      className="min-h-11 text-sm font-semibold text-[#A3262B] transition-colors hover:text-[#7F1D22]"
                    >
                      Esqueci minha senha
                    </button>
                  </div>

                  <button type="submit" disabled={isPending} className={primaryBtn}>
                    {isPending && (
                      <span className="size-4 animate-spin rounded-full border-2 border-white/35 border-t-white [animation-duration:.7s]" />
                    )}
                    {isPending ? 'Entrando...' : 'Entrar'}
                  </button>
                </form>

                <div className="flex items-center gap-3 text-xs text-[#6B6B70]">
                  <span className="h-px flex-1 bg-[rgba(11,11,12,.16)]" />
                  ou
                  <span className="h-px flex-1 bg-[rgba(11,11,12,.16)]" />
                </div>

                <Link
                  to="/"
                  className="flex h-12 items-center justify-center rounded-[12px] border border-[rgba(11,11,12,.16)] bg-white text-sm font-semibold text-[#0B0B0C] transition-colors hover:border-[rgba(11,11,12,.3)]"
                >
                  Voltar ao site do retiro
                </Link>
              </>
            ) : (
              <ForgotPassword email={email} setEmail={setEmail} onBack={() => switchMode('login')} />
            )}
          </div>
        </div>

        <footer className="mt-8 flex flex-wrap justify-between gap-2 text-xs text-[#6B6B70]">
          <span>© {new Date().getFullYear()} Comunidade Católica Apóstolos</span>
          <span>Acesso restrito à equipe</span>
        </footer>
      </main>
    </div>
  );
};

export default SignInPage;
